import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import '@/styles/permissionsStyles.scss';
import './RouterProvisioningSettingsPage.scss';
import {
  FaServer,
  FaSave,
  FaKey,
  FaNetworkWired,
  FaInfoCircle,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import sanitizeText from '@/utils/sanitizeText';
import * as Sentry from '@sentry/react';

/**
 * Router Provisioning Settings Page
 * 
 * Provides configuration for:
 * - RADIUS server settings for router authentication
 * - SNMP v3 settings for router monitoring
 * - Application server IP for firewall rules
 */
const RouterProvisioningSettingsPage = () => {
  const { apiFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Provisioning Settings
  const [settings, setSettings] = useState({
    radiusServerIp: '',
    radiusSharedSecret: '',
    radiusPort: 1812,
    radiusAccountingPort: 1813,
    snmpUsername: 'captrac-monitor',
    snmpAuthPassword: '',
    snmpPrivPassword: '',
    snmpAuthProtocol: 'SHA1',
    snmpPrivProtocol: 'AES',
    appServerIp: '',
    minimumRouterOSVersion: '7',
    lastModified: null
  });

  // Show/hide password fields
  const [showSecrets, setShowSecrets] = useState({
    radiusSecret: false,
    snmpAuth: false,
    snmpPriv: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  // Track whether settings have been loaded from API to avoid auto-saving on mount.
  // We use a counter: 0 = not loaded, 1 = initial load applied (skip), 2+ = user changed.
  const settingsChangeCountRef = useRef(0);
  const isSavingRef = useRef(false);

  // Auto-save settings with debounce — only fires after the user makes a change
  useEffect(() => {
    if (loading) return; // still loading, ignore
    settingsChangeCountRef.current += 1;
    // Skip the first change (initial API load populating state)
    if (settingsChangeCountRef.current <= 1) return;
    // Skip if this change was triggered by a save completing (re-load)
    if (isSavingRef.current) return;

    const timer = setTimeout(() => {
      saveSettings();
    }, 800);
    return () => clearTimeout(timer);
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Gets the current server address for auto-populating RADIUS/App Server fields
   * Uses window.location.hostname to derive the appropriate server address
   * @returns {string} The server hostname or IP address
   */
  const getServerAddress = () => {
    return window.location.hostname;
  };

  /**
   * Load provisioning settings from the API
   * Auto-populates RADIUS Server IP and Application Server IP if they are empty
   */
  const loadSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/RouterConfig/provisioning-settings');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const loadedSettings = data.data;
          
          // Auto-populate empty fields with current server address
          const serverAddress = getServerAddress();
          if (!loadedSettings.radiusServerIp || loadedSettings.radiusServerIp.trim() === '') {
            loadedSettings.radiusServerIp = serverAddress;
          }
          if (!loadedSettings.appServerIp || loadedSettings.appServerIp.trim() === '') {
            loadedSettings.appServerIp = serverAddress;
          }
          
          setSettings(loadedSettings);
        }
      } else {
        const errorData = await response.json();
        setError(sanitizeText(errorData.error || 'Failed to load settings'));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading provisioning settings:', err);
      setError(sanitizeText(err.message || 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save provisioning settings to the API
   */
  const saveSettings = async () => {
    setSaving(true);
    isSavingRef.current = true;
    setError(null);

    try {
      const response = await apiFetch('/api/RouterConfig/provisioning-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Provisioning settings saved successfully');
        // Update lastModified locally instead of reloading (which would re-trigger auto-save)
        if (data.data?.lastModified) {
          setSettings(prev => ({ ...prev, lastModified: data.data.lastModified }));
        }
      } else {
        toast.error(sanitizeText(data.error || 'Failed to save settings'));
        setError(sanitizeText(data.error || 'Failed to save settings'));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error saving provisioning settings:', err);
      const errorMsg = sanitizeText(err.message || 'Failed to save settings');
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  };

  /**
   * Handle input change for settings
   */
  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Toggle password visibility
   */
  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="main-content">
        <HexagonLoader />
      </div>
    );
  }

  return (
    <div className="main-content router-provisioning-settings-page">
      <div className="page-header">
        <h1><FaServer /> Router Provisioning Settings</h1>
        <p className="subtitle">
          Configure RADIUS and SNMP settings for automated router provisioning and management
        </p>
      </div>

      {error && (
        <div className="alert alert-danger">
          <FaInfoCircle /> {error}
        </div>
      )}

      {/* RADIUS Server Configuration */}
      <div className="settings-section">
        <div className="section-header">
          <h2><FaKey /> RADIUS Server Configuration</h2>
          <p className="section-description">
            RADIUS settings for router authentication. Routers will authenticate users against the application's user database.
          </p>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="radiusServerIp">
              RADIUS Server Address <span className="required">*</span>
            </label>
            <input
              type="text"
              id="radiusServerIp"
              value={settings.radiusServerIp}
              onChange={(e) => handleInputChange('radiusServerIp', e.target.value)}
              placeholder="10.0.0.100 or radius.example.com"
              className="form-control"
            />
            <small className="form-text">
              IP address or DNS hostname of the RADIUS server (this application server). 
              Both IPv4 addresses and fully qualified domain names (FQDN) are supported.
              <br />
              <strong>Auto-detected:</strong> Defaults to current server hostname (<code>{window.location.hostname}</code>) if empty.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="radiusSharedSecret">
              Shared Secret <span className="required">*</span>
            </label>
            <div className="input-with-icon">
              <input
                type={showSecrets.radiusSecret ? 'text' : 'password'}
                id="radiusSharedSecret"
                value={settings.radiusSharedSecret}
                onChange={(e) => handleInputChange('radiusSharedSecret', e.target.value)}
                placeholder="Enter strong shared secret"
                className="form-control"
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => toggleSecretVisibility('radiusSecret')}
                title={showSecrets.radiusSecret ? 'Hide secret' : 'Show secret'}
              >
                {showSecrets.radiusSecret ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <small className="form-text">Shared secret for RADIUS authentication (stored encrypted)</small>
          </div>

          <div className="form-group">
            <label htmlFor="radiusPort">
              Authentication Port
            </label>
            <input
              type="number"
              id="radiusPort"
              value={settings.radiusPort}
              onChange={(e) => handleInputChange('radiusPort', parseInt(e.target.value))}
              className="form-control"
              min="1"
              max="65535"
            />
            <small className="form-text">Default: 1812 (RADIUS authentication)</small>
          </div>

          <div className="form-group">
            <label htmlFor="radiusAccountingPort">
              Accounting Port
            </label>
            <input
              type="number"
              id="radiusAccountingPort"
              value={settings.radiusAccountingPort}
              onChange={(e) => handleInputChange('radiusAccountingPort', parseInt(e.target.value))}
              className="form-control"
              min="1"
              max="65535"
            />
            <small className="form-text">Default: 1813 (RADIUS accounting)</small>
          </div>
        </div>
      </div>

      {/* SNMP v3 Configuration */}
      <div className="settings-section">
        <div className="section-header">
          <h2><FaNetworkWired /> SNMP v3 Configuration</h2>
          <p className="section-description">
            SNMP v3 settings for router monitoring. These credentials will be configured on all provisioned routers.
          </p>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="snmpUsername">
              SNMP Username <span className="required">*</span>
            </label>
            <input
              type="text"
              id="snmpUsername"
              value={settings.snmpUsername}
              onChange={(e) => handleInputChange('snmpUsername', e.target.value)}
              placeholder="captrac-monitor"
              className="form-control"
            />
            <small className="form-text">SNMP v3 username for monitoring</small>
          </div>

          <div className="form-group">
            <label htmlFor="snmpAuthPassword">
              Authentication Password <span className="required">*</span>
            </label>
            <div className="input-with-icon">
              <input
                type={showSecrets.snmpAuth ? 'text' : 'password'}
                id="snmpAuthPassword"
                value={settings.snmpAuthPassword}
                onChange={(e) => handleInputChange('snmpAuthPassword', e.target.value)}
                placeholder="Enter auth password"
                className="form-control"
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => toggleSecretVisibility('snmpAuth')}
                title={showSecrets.snmpAuth ? 'Hide password' : 'Show password'}
              >
                {showSecrets.snmpAuth ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <small className="form-text">SNMP v3 authentication password (stored encrypted)</small>
          </div>

          <div className="form-group">
            <label htmlFor="snmpPrivPassword">
              Privacy Password <span className="required">*</span>
            </label>
            <div className="input-with-icon">
              <input
                type={showSecrets.snmpPriv ? 'text' : 'password'}
                id="snmpPrivPassword"
                value={settings.snmpPrivPassword}
                onChange={(e) => handleInputChange('snmpPrivPassword', e.target.value)}
                placeholder="Enter privacy password"
                className="form-control"
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => toggleSecretVisibility('snmpPriv')}
                title={showSecrets.snmpPriv ? 'Hide password' : 'Show password'}
              >
                {showSecrets.snmpPriv ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <small className="form-text">SNMP v3 privacy password (stored encrypted)</small>
          </div>

          <div className="form-group">
            <label htmlFor="snmpAuthProtocol">
              Authentication Protocol
            </label>
            <select
              id="snmpAuthProtocol"
              value={settings.snmpAuthProtocol}
              onChange={(e) => handleInputChange('snmpAuthProtocol', e.target.value)}
              className="form-control"
            >
              <option value="SHA1">SHA1</option>
              <option value="MD5">MD5</option>
            </select>
            <small className="form-text">Recommended: SHA1</small>
          </div>

          <div className="form-group">
            <label htmlFor="snmpPrivProtocol">
              Privacy Protocol
            </label>
            <select
              id="snmpPrivProtocol"
              value={settings.snmpPrivProtocol}
              onChange={(e) => handleInputChange('snmpPrivProtocol', e.target.value)}
              className="form-control"
            >
              <option value="AES">AES</option>
              <option value="DES">DES</option>
            </select>
            <small className="form-text">Recommended: AES</small>
          </div>
        </div>
      </div>

      {/* Application Server */}
      <div className="settings-section">
        <div className="section-header">
          <h2><FaServer /> Application Server</h2>
          <p className="section-description">
            Application server IP address used for configuring firewall rules on routers.
          </p>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="appServerIp">
              Server IP Address <span className="required">*</span>
            </label>
            <input
              type="text"
              id="appServerIp"
              value={settings.appServerIp}
              onChange={(e) => handleInputChange('appServerIp', e.target.value)}
              placeholder="10.0.0.100"
              className="form-control"
            />
            <small className="form-text">
              IP address of this application server (used for SNMP and API access firewall rules)
              <br />
              <strong>Auto-detected:</strong> Defaults to current server hostname (<code>{window.location.hostname}</code>) if empty.
            </small>
          </div>
        </div>
      </div>

      {/* RouterOS Version Management */}
      <div className="settings-section">
        <div className="section-header">
          <h2><FaNetworkWired /> RouterOS Version Management</h2>
          <p className="section-description">
            Configure the minimum required RouterOS version for managed routers.
            Routers below this version will be automatically upgraded during provisioning.
          </p>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="minimumRouterOSVersion">
              Minimum RouterOS Major Version <span className="required">*</span>
            </label>
            <input
              type="text"
              id="minimumRouterOSVersion"
              value={settings.minimumRouterOSVersion}
              onChange={(e) => handleInputChange('minimumRouterOSVersion', e.target.value)}
              placeholder="7"
              className="form-control"
            />
            <small className="form-text">
              Minimum major version required (e.g., "7" for RouterOS 7.x, "8" for RouterOS 8.x).
              <br />
              During provisioning, routers will be checked and upgraded if running an older version.
              <br />
              <strong>Note:</strong> Upgrades require router reboots and may take several minutes.
            </small>
          </div>
        </div>
      </div>

      {/* Last Modified Info */}
      <div className="settings-actions">
        {saving && (
          <div style={{ color: '#2563eb', fontSize: '0.85rem' }}>
            <FaSave /> Saving...
          </div>
        )}

        {settings.lastModified && (
          <div className="last-modified-info">
            <FaInfoCircle /> Last modified: {new Date(settings.lastModified).toLocaleString()}
          </div>
        )}
      </div>

      {/* Important Notes */}
      <div className="info-box">
        <h3><FaInfoCircle /> Important Notes</h3>
        <ul>
          <li>All passwords are encrypted before being stored in the database</li>
          <li>Changes take effect immediately for new router provisioning operations</li>
          <li>Ensure the RADIUS server ports (1812, 1813) are open in your firewall</li>
          <li>The application server IP must be accessible from all routers</li>
          <li>SNMP v3 provides strong authentication and encryption for monitoring data</li>
        </ul>
      </div>
    </div>
  );
};

export default RouterProvisioningSettingsPage;
